import {Component, HostBinding, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import * as fromStore from "../../../store";
import {DbStoreState} from "../../../store";
import {Store} from '@ngrx/store';
import {RouteDataService} from "../../../shared/services/route-data/route-data.service";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from "rxjs/operators";
import {User} from "../../../user/user";
import {combineLatest, Subject} from "rxjs";
import {UserSubscription, UserSubscriptionIds, UserSubscriptionType} from "../../../user/user-subscription";
import {Config} from "../../../config/config";
import {DbResponse} from "../../../shared/interceptors/db-response";
import {UserMapper} from "../../../user/user-mapper";
import {SignUpFor, TypeFormAnswers} from "../../../add-website/typeFormAnswers";
import {UserService} from "../../../user/user.service";
import {AuthService} from "../../../auth/auth.service";
import {GlobalEvent} from "../../../shared/services/global-event-dispatcher/global-event";
import {
  GlobalEventDispatcherService
} from "../../../shared/services/global-event-dispatcher/global-event-dispatcher.service";
import blocksInfo from "./setup-blocks-info.json";
import {Website} from "../../../websites/website/website";
import {preventInitial, slideLeftEnterAnimation, slideLeftLeaveAnimation} from "../../../shared/utils/animations";
import {BuilderTemplates} from "../../../websites/website/builder-templates/builder-templates";
import {PluginConnectService} from "../../../shared/services/plugin-connect/plugin-connect.service";
import {
  CreateAiAssistantSignUpComponent
} from "./create-ai/create-ai-assistant/create-ai-assistant-sign-up/create-ai-assistant-sign-up.component";
import {DbModalService} from "../../../modules/modal/modal";
import {Location} from '@angular/common';
import {CookieService} from "ngx-cookie";
import {TextUtils} from "../../../shared/utils/text-utils";
import steps from "./signup-steps.json";
import {BuilderHistoryService} from "../../../builder-v2/builder-history.service";
import {BuilderSectionsInteractionService} from "../../../builder-v2/builder-sections-interaction.service";
import {BuilderStepsEnum} from "../../../builder-v2/builder-tour.enum";
import {CreateAiAssistantService} from "./create-ai/create-ai-assistant/create-ai-assistant.service";

@Component({
    selector: 'db-setup',
    templateUrl: './setup.component.html',
    styleUrls: ['./setup.component.scss'],
    animations: [slideLeftEnterAnimation, slideLeftLeaveAnimation, preventInitial],
    standalone: false
})

export class SetupComponent implements OnInit, OnDestroy {

  /** Preventing Initial Animations */
  @HostBinding('@preventInitial')
  protected readonly BuilderStepsEnum = BuilderStepsEnum;
  protected readonly builderStep = BuilderStepsEnum;
  public UserSubscriptionType: any = UserSubscriptionType;
  public pageId;
  public user: User;
  public blocks = blocksInfo;
  public currentSubscription: UserSubscription;
  public formId: string;
  public signUpForAnswers: SignUpFor;
  public userLocalStorage: any;
  public oneToThreeWebsites: boolean;
  public typeFormAnswers = TypeFormAnswers;
  public signUpForStep: boolean;
  public firstChosenOption: string;
  public hideBackButton: boolean = false;
  public isSent: boolean = false;
  public userSubscriptionIds = UserSubscriptionIds;
  public photoGalleryPluginIds = Config.photoGalleryPluginIds;
  public demoUser: boolean = false;
  public fromAiCreate: boolean = false;
  public isMobile: boolean = false;
  public steps = steps;
  public emailCampaign: boolean = false;

  private navigateTo = '';
  private companyData: string = '';

  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  builderSteps = BuilderStepsEnum;

  constructor(
    public _builderHistoryService: BuilderHistoryService,
    public createAiAssistantService: CreateAiAssistantService,
    private _route: ActivatedRoute,
    private _store: Store<DbStoreState>,
    private _router: Router,
    private _routeDataService: RouteDataService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _userService: UserService,
    private _authService: AuthService,
    private _globalEventDispatcher: GlobalEventDispatcherService,
    private _pluginConnectService: PluginConnectService,
    private _modalService: DbModalService,
    private _location: Location,
    private _cookieService: CookieService,
    public _builderSectionsInteractionsService: BuilderSectionsInteractionService
  ) {
  }

  ngOnInit() {
    const queryParams: any = (<any>this._route.queryParams).value;
    this.emailCampaign = !!(queryParams.emailCampaign || new URL(window.location.href).searchParams.get("emailCampaign")) && !window.location.href.includes('setup-onboarding');

    if (queryParams.product_id) {
      localStorage.setItem('product_id', queryParams.product_id.toString());
      const productId = parseInt(queryParams.product_id, 10);

      if (productId === Config.aiCreateWebsitePluginId) {
        this._showSignUpComponent(true, false);
      } else if (productId === Config.managerPluginId) {
        this._showSignUpComponent(false, true);
      } else if (productId === Config.installBlankPluginId) {
        this._showSignUpComponent(false, false);
      }
    }

    this.isMobile = window.innerWidth <= 991;
    this._globalEventDispatcher.addEventListener(GlobalEvent.hideOnboardingBackButton, this.onHideOnboardingBackButton, this);
    this.currentSubscription = this._route.snapshot.data['currentSubscription'];
    this.user = this._route.snapshot.data['user'];
    if (this.emailCampaign && this.currentSubscription?.hostingTrialExpired) {
      this._userService.deleteExpiration().pipe(
        first(response => !!response, null),
      ).subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserSubscription());
          this._store.dispatch(new fromStore.LoadUser());
        } else {
          this._router.navigate(['/upgrade-plan']);
        }
      });
    }
    this.userLocalStorage = this._route.snapshot.data['addWebsiteResolver'] || {};
    this.signUpForAnswers = this.user && this.user.signUpFor;
    this.signUpForStep = false; // this.user && !this.user.signUpFor;
    this.formId = this.currentSubscription && this.currentSubscription.product_id === '200' ? Config.typeFormExpertId : Config.typeFormOnboardingId;
    this.pageId = this._routeDataService.getCurrentRouteData().pageId;

    if (this.pageId === 'convert' && parseInt(localStorage.getItem('product_id'), 10) === Config.aiCreateWebsitePluginId) {
      this.hideBackButton = true;
    } else if (this.pageId === 'move-hosting' && parseInt(localStorage.getItem('product_id'), 10) === Config.managerPluginId) {
      this.hideBackButton = true;
    } else if (this.pageId === 'install-blank' && parseInt(localStorage.getItem('product_id'), 10) === Config.installBlankPluginId) {
      this.hideBackButton = true;
    } else {
      this.hideBackButton = false;
    }
    this.navigateTo = this._route.snapshot.queryParams['navigate_to'];
    this.companyData = this._route.snapshot.queryParams['company_info'];
    if (this.pageId === 'setup' || this.pageId === 'setup-onboarding') {
      localStorage.removeItem('showFinalize');
    }
    const productId = parseInt(localStorage.getItem('product_id'), 10);
    this.user && !productId && !this.user.signUpFor && this._authService.updateSignUpFor({}, true)
      .pipe(
        first(response => !!response, null),
      )
      .subscribe(() => {
        this._store.dispatch(new fromStore.LoadUser());
        if (this.user && this.currentSubscription && !Config.photoGalleryPluginIds.includes(this.currentSubscription.product_id) && this._pluginConnectService.pluginData) {
          this._pluginConnectService.connect(this.user.email);
        } else {
          this.closeSignUpForStep();
        }
        this._userService.setToLocal(this.userLocalStorage)
          .pipe(
            first(response => !!response, null),
          )
          .subscribe(response => {
            if (response.success) {
              this._store.dispatch(new fromStore.LoadUserLocalStorage());
            }
          });
      });
    this.routerEvents();
    this.getUser();
  }


  public routerEvents(): void {
    this._router.events
      .pipe(
        takeUntil(this._destroyStream$),
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        const queryParamsIndex: number = this._router.url.indexOf('?');
        if (queryParamsIndex !== -1) {
          this.pageId = this._router && this._router.url.substring(0, queryParamsIndex).split("/").pop();
        } else {
          this.pageId = this._router && this._router.url.split("/").pop();
        }

        if (this.pageId === 'ai-convert' && parseInt(localStorage.getItem('product_id'), 10) === Config.aiCreateWebsitePluginId) {
          this.hideBackButton = true;
        } else if (this.pageId === 'change-hosting' && parseInt(localStorage.getItem('product_id'), 10) === Config.managerPluginId) {
          this.hideBackButton = true;
        } else this.hideBackButton = this.pageId === 'install-blank' && parseInt(localStorage.getItem('product_id'), 10) === Config.installBlankPluginId;
      });
  }

  public getUser(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        first(loaded => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserLocalStorage),
          this._store.select(fromStore.getUserSubscription),
          this._store.select(fromStore.getWebsites)
        )),
        distinctUntilChanged(([prevUser], [currUser]) => prevUser && currUser && JSON.stringify(prevUser.signUpFor) === JSON.stringify(currUser.signUpFor)),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, userLocalStorage, currentSubscription, websites]) => {
        this.user = user;
        this.userLocalStorage = userLocalStorage;
        this.currentSubscription = currentSubscription;
        this.demoUser = !!(this.currentSubscription && Config.aiBuilderPluginIds.includes(this.currentSubscription.product_id)
          && [Config.defaultStarterPlanId, Config.basePlanId].includes(+this.currentSubscription.subscriptionId));

        if (this.user && this.user.signUpFor && this.user.signUpFor['LastChosenOption'] && !localStorage.getItem("prefilledAiData")
          && ['ai-demo-section', 'ai-demo-section-after-signup', 'ai-demo-template', 'ai-demo-template-after-signup'].includes(this.user.signUpFor['LastChosenOption'].answer) && websites && websites.length) {
          this.goToNextStep(+this.currentSubscription?.product_id === Config.builder2PluginId ?
            'builder-v2' : 'ai-demo', this.currentSubscription?.product_id === Config.builder2PluginId ?
            'Create a Website with AI BuilderV2' : 'Create a Website with AI Builder');
          this.hideBackButton = false;
        }
        this.boosterFlow();
      });
    this.getOngoingActivities();
  }


  public closeSignUpForStep() {
    if (this.currentSubscription.trialBoosterClient
      && (!this.photoGalleryPluginIds.includes(this.currentSubscription.product_id) || !this._pluginConnectService.pluginData || !this._pluginConnectService.pluginData.returnUrl)) {
      if (this.navigateTo) {
        return this._router.navigate([`/${this.navigateTo}`]);
      }
      return this._router.navigate(['/websites']);
    }
    this.signUpForStep = false;
    return null;
  }


  public onHideOnboardingBackButton(hide) {
    this.hideBackButton = !!hide;
  }

  private _getLastImportedTemplate(websiteId): void {
    this._store.select(fromStore.getLastBuilderTemplateLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getLastBuilderTemplate(websiteId)))
      )
      .subscribe((builderTemplate: BuilderTemplates) => {
        if (builderTemplate && (builderTemplate.type === "last_recreated" || builderTemplate.id)) {
          this.hideBackButton = true;
        }
      });
  }

  private submitTypeForm = (data) => {
    this._userService.sendTypeFormAnswerId(data.response_id, this.formId)
      .pipe(
        filter((response: DbResponse): boolean => !!response),
        first()
      )
      .subscribe((response: DbResponse): void => {
        let signUpFor,
          _dataRole,
          _dataWebsiteCount;

        if (response.success) {
          if (response.data) {
            try {
              signUpFor = JSON.parse(response.data);
            } catch (e) {
              signUpFor = null;
            }
          }
          this.signUpForAnswers = signUpFor && UserMapper.signUpForMapper(signUpFor) || null;
          if (this.signUpForAnswers) {
            if (this.signUpForAnswers.Role && this.signUpForAnswers.Role.id) {
              _dataRole = this.signUpForAnswers.Role.id;
            }
            if (this.signUpForAnswers.WebsiteNumber && this.signUpForAnswers.WebsiteNumber.id) {
              _dataWebsiteCount = this.signUpForAnswers.WebsiteNumber.id;
            }
          }
        }

        if (_dataRole) {
          this.userLocalStorage['signUpFor'] = _dataRole;
        }
        if (this.signUpForAnswers && this.signUpForAnswers.Role && this.signUpForAnswers.WebsiteNumber) {

          this.oneToThreeWebsites = ([this.typeFormAnswers.AGENCY, this.typeFormAnswers.FREELANCE].includes(_dataRole) || this.user.agencyOrFreelancer) &&
            [this.typeFormAnswers.FROM_1_TO_3].includes(_dataWebsiteCount);
        }

        this._store.dispatch(new fromStore.LoadUser());
        this._userService.setToLocal(this.userLocalStorage)
          .pipe(
            filter((_response: DbResponse): boolean => !!_response),
            first()
          )
          .subscribe((_response: DbResponse): void => {
            if (_response.success) {
              this._store.dispatch(new fromStore.LoadUserLocalStorage());
            }
            this.signUpForStep = false;
          });
      });
  }

  public goToNextStep(url: string, gaAction: string, label?: string) {
    if (!(localStorage.getItem('dbShowV2Finalize'))) {
      this._resetBuilderV2();
    }
    this._store.dispatch(new fromStore.ToggleKnowledgeBase(false));
    if (this.user && this.user.signUpFor) {
      if (!this.firstChosenOption && !this.user.signUpFor.FirstChosenOption) {
        this.firstChosenOption = url;
        this.user.signUpFor['FirstChosenOption'] = {answer: url, id: ''};
      }
      url !== 'ai-demo' && (this.user.signUpFor['LastChosenOption'] = {answer: url, id: ''});
      this._authService.updateSignUpFor(this.user.signUpFor, true)
        .pipe(
          first(response => !!response),
        )
        .subscribe(() => {
          this._store.dispatch(new fromStore.LoadUser());
        });
    }
    localStorage.removeItem('dbOnboardingDomainName');
    localStorage.removeItem('dbFromDomains');
    url !== 'builder-v2' && this.choseStepEvent(gaAction, label);
    if (['install-blank', 'change-hosting', 'ai-convert'].includes(url)) {
      if (!this.user) {
        const productId = (url === 'ai-convert') ? Config.aiCreateWebsitePluginId : (url === 'change-hosting') ? Config.managerPluginId : Config.installBlankPluginId;
        localStorage.setItem('product_id', productId.toString());
        this._showSignUpComponent(url === 'ai-convert', url === 'change-hosting');
        return;
      }
    }

    if (!this.user && url === 'start-ai-website-building') {
      window.location.href = 'https://10web.io/ai-website-builder/';
    } else {
      this._router.navigate([url], {queryParams: {company_info: this.companyData}, relativeTo: this._route});
    }
  }

  public choseStepEvent(url, label?): void {
    const analyticsData = {
      eventCategory: this.user ? 'Onboarding' : 'button_click',
      eventAction: this.user ? url : 'ai-builder-onboarding',
      eventLabel: label ? label : 'View'
    };

    if (this.user) {
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    } else {
      this._googleAnalyticsService.sendAnalyticsEventWithCoreWithoutUserData(analyticsData)
        .pipe(filter((response: DbResponse): boolean => !!response),
          first())
        .subscribe((response: DbResponse): void => {
          if (response.success && response.data.tenweb_cookie && !this._cookieService.get('FPID')) {
            this._cookieService.put('FPID', response.data.tenweb_cookie, {path: '/', domain: '10web.io'});
          }
        });
    }
  }


  public boosterFlow(): void {
    if (this.currentSubscription?.trialBoosterClient && this.user && this.user.signUpFor) {
      this.user.signUpFor['Action'] = {answer: 'booster', id: ''};
      this._authService.updateSignUpFor(this.user.signUpFor, true);
    }
  }

  public openChat(hasOnlineAgentInChat): void {
    if (hasOnlineAgentInChat) {
      /* window['zE']('messenger', 'chat:removeTags', ['generic_support']);
       window['zE']('messenger', 'chat:addTags', ['skip_dashboard']);*/

      window['Smooch']?.open();
      /*window['zE']('messenger', 'chat:send', 'Skip to Dashboard');*/
      this._store.dispatch(new fromStore.ToggleChatOverlay(false));
    }
    this._router.navigate(['/websites'])
      .then(() => this._store.dispatch(new fromStore.LoadUser()));
  }

  public getOngoingActivities(): void {
    const queryParams: any = (<any>this._route.queryParams).value;
    const fromGoogleSignIn = queryParams.fromGoogleSignIn;

    this._store
      .select(fromStore.getWebsitesLoaded)
      .pipe(
        filter((loaded: boolean): boolean => !!loaded),
        switchMap(() => this._store.select(fromStore.getWebsites)),
        takeUntil(this._destroyStream$)
      )
      .subscribe((websites: Website[]) => {
        const lastWebsiteId = websites.length && websites.length - 1;
        if (websites && websites.length && this.currentSubscription && websites[lastWebsiteId] && !websites[lastWebsiteId].domain_hash) {
          if (websites && websites.length && websites[lastWebsiteId]) {
            this._getLastImportedTemplate(websites[lastWebsiteId].id);
          }
          this._router.navigate(['/websites'], {replaceUrl: true});
        }
      });
  }

  public back() {
    const backAction = this._builderSectionsInteractionsService.getBackAction();
    if (backAction) {
      backAction();
    } else {
      history.back();
    }
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _showSignUpComponent(isConvert: boolean = false, isMigrate: boolean = false, isBuilder2: boolean = false): void {
    const options = {
      hasCloseBtn: true,
      overflowHidden: false,
    };
    if (!this.isMobile) {
      options['width'] = 900;
      options['height'] = 500;
    }
    const modal = this._modalService.open(CreateAiAssistantSignUpComponent, options);
    modal.componentInstance.forSetup = true;
    modal.componentInstance.title = TextUtils.getText(isConvert ? 'text_convertAISignUpPopUpTitle' : isMigrate ? 'text_setupChangeHostingBlockTitle' : isBuilder2 ? 'text_builder2SignUpPopUpTitle' : 'text_signUpTitle');
    modal.componentInstance.desc = (isMigrate || isConvert || isBuilder2) ? TextUtils.getText(isConvert ? 'text_convertAISignUpPopUpDesc' : (isBuilder2 ? 'text_builder2SignUpPopUpDesc' : 'text_migrateAISignUpPopUpDesc')) : null;
    modal.componentInstance.steps = isMigrate ? this.steps['migrateSteps'] : isConvert ? this.steps['convertSteps'] : isBuilder2 ? this.steps['builder2Steps'] : this.steps['installBlankSteps'];
    modal.dismissed.subscribe(() => {
      if (localStorage.getItem('product_id')) {
        localStorage.removeItem('product_id');
      }
    });
    modal.closed.subscribe(() => {
      this._store.dispatch(new fromStore.LoadUser());
    });
  }

  public toggleKnowledgeBase(articleId) {
    this._store.dispatch(new fromStore.ToggleKnowledgeBase({articleId: articleId, scrollToEl: ''}));
  }

  private _resetBuilderV2() {
    if (localStorage.getItem("builder_history")) {
      localStorage.removeItem("builder_history");
      localStorage.removeItem("builderType");
    }
    this._builderHistoryService.resetSubjects();
    this._builderSectionsInteractionsService.resetSubjects();
  }


}
